import React, { useCallback, useMemo } from 'react';
import { notification } from 'antd';
import { EVENTS } from 'shared/constants';
import { useAddKnowledge, useRemoveKnowledge } from 'shared/hooks/rotations.hooks';
import { useRotationsContext } from '../rotations-context';
import analyticsService from 'src/helpers/analytics.service';
import ResourceManager from 'src/components/core/select-content/resource-manager.component';

const KnowledgeTab = () => {
  const { lang, selectedRotation: rotation, canEdit, analyticsTriggers } = useRotationsContext();
  const addKnowledge = useAddKnowledge();
  const removeKnowledge = useRemoveKnowledge();

  const [api, contextHolder] = notification.useNotification();

  const rotationResourceIds = useMemo(() => rotation?.knowledge?.map(k => k.id) || [], [rotation]);

  const showErrorToast = useCallback(() => {
    api.error({
      title: lang.ERROR_OCCURRED,
      placement: 'bottomRight',
    });
  }, [api, lang.ERROR_OCCURRED]);

  const showSuccessToast = useCallback(
    message => {
      api.success({
        title: message,
        placement: 'bottomRight',
      });
    },
    [api],
  );

  const doAdd = useCallback(
    async payload => {
      try {
        const data = await addKnowledge.mutateAsync({
          rotationId: rotation?.id,
          payload,
        });
        showSuccessToast(lang.RESOURCE_ADDED);
        analyticsTriggers?.onResourceAdded({
          id: rotation?.id,
          resourceId: data?.id ?? payload.id,
        });
      } catch (e) {
        showErrorToast();
      }
    },
    [
      addKnowledge,
      rotation?.id,
      showErrorToast,
      showSuccessToast,
      lang.RESOURCE_ADDED,
      analyticsTriggers,
    ],
  );

  const doRemove = useCallback(
    async ({ id }) => {
      try {
        await removeKnowledge.mutateAsync({ rotationId: rotation?.id, id });
        showSuccessToast(lang.RESOURCE_REMOVED);
        analyticsTriggers?.onResourceDeleted({ id: rotation?.id, resourceId: id });
      } catch (e) {
        showErrorToast();
      }
    },
    [
      removeKnowledge,
      rotation?.id,
      showErrorToast,
      showSuccessToast,
      lang.RESOURCE_REMOVED,
      analyticsTriggers,
    ],
  );

  const sendAnalytics = useCallback(
    (event, item) => {
      analyticsService.track(event, {
        rotationId: rotation?.id,
        rotationName: rotation?.name,
        ...item,
      });
    },
    [rotation],
  );

  const resourceAnalyticsTriggers = useMemo(
    () => ({
      add: item => sendAnalytics(EVENTS.ROTATIONS.ADD_RESOURCE, item),
      remove: item => sendAnalytics(EVENTS.ROTATIONS.DELETE_RESOURCE, item),
      open: item => sendAnalytics(EVENTS.ROTATIONS.RESOURCE_OPEN, item),
    }),
    [sendAnalytics],
  );

  return (
    <ResourceManager
      resources={rotation?.knowledge}
      canEdit={canEdit}
      notFoundText={lang.NO_RESOURCES_FOUND}
      onAddResource={doAdd}
      onRemoveResource={doRemove}
      analyticsTriggers={resourceAnalyticsTriggers}
      modalTitle={lang.ADD_RESOURCE}
      disabledResourceIds={rotationResourceIds}
      contextHolder={contextHolder}
      lang={lang}
      showNotFoundPanda
    />
  );
};

export { KnowledgeTab };
